import React, { useCallback, useEffect, useState } from 'react';
import { Users, CalendarPlus, Check, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { aaiApi } from '../../../services/aaiApi';
import type { Cohort, AaiSession } from '../../../types/aai';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel } from '../dashboards/ui';

const SESSION_TYPES = ['coaching', 'quick-win', 'deep-dive'];

// Trainer "Delivery" — this week's sessions across cohorts and 1:1s. Schedule a
// session, track attendance, and leave a skill behind. Cohorts are built on the
// Cohorts tab; the schedule picker just references them.
const DeliveryPage: React.FC = () => {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [sessions, setSessions] = useState<AaiSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // schedule form
  const [form, setForm] = useState({ cohort_id: '', title: '', session_type: 'quick-win', scheduled_at: '' });
  // inline attendance editor
  const [att, setAtt] = useState<{ sessionId: string; members: string[]; map: Record<string, boolean> } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, ss] = await Promise.all([aaiApi.listCohorts(), aaiApi.listSessions()]);
      setCohorts(c.data.cohorts); setSessions(ss.data.sessions); setError(null);
    } catch { setError('Could not load delivery.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const schedule = async () => {
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      await aaiApi.scheduleSession({
        cohort_id: form.cohort_id || undefined, title: form.title.trim(), session_type: form.session_type,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : undefined,
      });
      setForm({ cohort_id: '', title: '', session_type: 'quick-win', scheduled_at: '' });
      await load();
    } finally { setBusy(false); }
  };

  const openAttendance = async (s: AaiSession) => {
    if (!s.cohort_id) return;
    setBusy(true);
    try {
      const [cohort, attendance] = await Promise.all([aaiApi.getCohort(s.cohort_id), aaiApi.getAttendance(s.id)]);
      const map: Record<string, boolean> = {};
      attendance.data.attendance.forEach((a) => { map[a.member_email] = a.attended; });
      setAtt({ sessionId: s.id, members: cohort.data.members, map });
    } finally { setBusy(false); }
  };
  const saveAttendance = async () => {
    if (!att) return;
    setBusy(true);
    try { await aaiApi.recordAttendance(att.sessionId, att.map); setAtt(null); await load(); }
    finally { setBusy(false); }
  };

  // Skills live in the Skills Marketplace (CoE structure, Feature 4) — a delivery
  // session is coaching, so completing it just marks it done, no skill authored here.
  const completeSession = async (sessionId: string) => {
    setBusy(true);
    try { await aaiApi.setDeliverable(sessionId, { deliverable_type: 'coaching' }); await load(); }
    finally { setBusy(false); }
  };

  if (loading) return <Loading label="Loading delivery…" />;
  if (error) return <ErrorNote message={error} />;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-1"><Users size={22} className="text-brand-500" /><h1 className="text-2xl font-semibold text-gray-900">Delivery</h1></div>
      <p className="text-sm text-gray-500 mb-6">This week's sessions across cohorts and 1:1s — schedule one, track attendance, and leave a skill behind. Build cohorts on the <span className="font-medium text-gray-600">Cohorts</span> tab.</p>

      {/* schedule a session */}
      <Card className="mb-4">
        <MonoLabel className="mb-3">Schedule a session</MonoLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Session title (e.g. Excel + AI quick-win)"
            className="rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200 sm:col-span-2" />
          <select value={form.cohort_id} onChange={(e) => setForm({ ...form, cohort_id: e.target.value })} className="rounded-lg border border-gray-200 px-2 py-2 text-[13px]">
            <option value="">No cohort (1:1 or open)</option>
            {cohorts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={form.session_type} onChange={(e) => setForm({ ...form, session_type: e.target.value })} className="rounded-lg border border-gray-200 px-2 py-2 text-[13px]">
            {SESSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
            className="rounded-lg border border-gray-200 px-3 py-2 text-[13px] sm:col-span-2" />
        </div>
        <Button variant="brand" onClick={schedule} disabled={busy || !form.title.trim()}><CalendarPlus size={15} className="mr-1.5" />Schedule</Button>
      </Card>

      {/* sessions */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 font-semibold text-sm">Sessions</div>
        {sessions.length === 0 && <p className="px-5 py-6 text-[13px] text-gray-400">No sessions scheduled yet.</p>}
        {sessions.map((s) => (
          <div key={s.id} className="px-5 py-3.5 border-b border-gray-50 last:border-0">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-medium text-gray-900">{s.title}</div>
                <div className="text-[11.5px] text-gray-500">
                  <span className="font-sans uppercase">{s.session_type}</span>
                  {s.duration_min ? <> · {s.duration_min} min</> : null}
                  {s.cohort_name && <> · {s.cohort_name}</>}
                  {s.scheduled_at && <> · {new Date(s.scheduled_at).toLocaleDateString()}</>}
                  {s.module_title && <> · covers {s.module_title}{s.module_week ? ` (${s.module_week})` : ''}</>}
                </div>
              </div>
              {s.status === 'completed' ? (
                <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1"><Check size={13} /> Completed</span>
              ) : (
                <div className="flex items-center gap-2">
                  {s.cohort_id && <button disabled={busy} onClick={() => openAttendance(s)} className="text-[11.5px] font-semibold bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-lg disabled:opacity-50">Attendance</button>}
                  <button disabled={busy} onClick={() => completeSession(s.id)} className="text-[11.5px] font-semibold bg-brand-500 hover:bg-brand-600 text-white px-2.5 py-1.5 rounded-lg disabled:opacity-50">Mark complete</button>
                </div>
              )}
            </div>

            {att && att.sessionId === s.id && (
              <div className="mt-3 bg-gray-50 rounded-lg p-3">
                <div className="text-[11.5px] font-semibold text-gray-600 mb-2">Who attended?</div>
                {att.members.map((m) => (
                  <label key={m} className="flex items-center gap-2 py-1 text-[13px]">
                    <input type="checkbox" checked={!!att.map[m]} onChange={(e) => setAtt({ ...att, map: { ...att.map, [m]: e.target.checked } })} />
                    {m}
                  </label>
                ))}
                {att.members.length === 0 && <p className="text-[12px] text-gray-400">This cohort has no members.</p>}
                <div className="mt-2 flex gap-2">
                  <button disabled={busy} onClick={saveAttendance} className="text-[12px] font-semibold bg-brand-500 text-white px-3 py-1.5 rounded-lg disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : 'Save'}</button>
                  <button onClick={() => setAtt(null)} className="text-[12px] text-gray-500">Cancel</button>
                </div>
              </div>
            )}

          </div>
        ))}
      </div>
    </div>
  );
};

export default DeliveryPage;
